/**
 * dashboardApi — pure axios functions for dashboard endpoints.
 *
 * These functions are intentionally side-effect free so they can be used
 * directly in React Query hooks.
 */

import api from "./api_instance";
import { API_ENDPOINTS } from "../constants/api";
import {
  DashboardByCodeResponse,
  DashboardListResponse,
  PermanentDeleteDashboardResponse,
  RecycleBinDashboardsResponse,
  RestoreDashboardResponse,
} from "../types/dashboard";

export type DashboardWidgetExportFormat = "xlsx" | "csv";

const withParams = (
  endpoint: string,
  params: Record<string, string | number>,
): string =>
  Object.entries(params).reduce(
    (resolved, [key, value]) =>
      resolved.replace(`:${key}`, encodeURIComponent(value)),
    endpoint,
  );

/** GET /dashboards */
export const getDashboardsApi = async (): Promise<DashboardListResponse> => {
  const { data } = await api.get<DashboardListResponse>(
    API_ENDPOINTS.DASHBOARDS.LIST,
  );
  return data;
};

/** GET /dashboards/recycle-bin */
export const getRecycleBinDashboardsApi =
  async (): Promise<RecycleBinDashboardsResponse> => {
    const { data } = await api.get<RecycleBinDashboardsResponse>(
      API_ENDPOINTS.DASHBOARDS.RECYCLE_BIN,
    );
    return data;
  };

/** POST /dashboards/:dashboard_code/restore */
export const restoreDashboardApi = async (
  dashboardCode: string,
): Promise<RestoreDashboardResponse> => {
  const { data } = await api.post<RestoreDashboardResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.RESTORE, {
      dashboard_code: dashboardCode,
    }),
  );
  return data;
};

/**
 * DELETE /dashboards/:dashboard_code/permanent — hard-deletes a dashboard
 * already in the recycle bin. Irreversible; 404 if it's not currently
 * soft-deleted (e.g. never deleted, already restored, or already purged).
 */
export const permanentlyDeleteDashboardApi = async (
  dashboardCode: string,
): Promise<PermanentDeleteDashboardResponse> => {
  const { data } = await api.delete<PermanentDeleteDashboardResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.PERMANENT_DELETE, {
      dashboard_code: dashboardCode,
    }),
  );
  return data;
};

/** GET /dashboards/layout-patterns */
export const getDashboardLayoutPatternsApi = async <
  TResponse = unknown,
>(): Promise<TResponse> => {
  const { data } = await api.get<TResponse>(
    API_ENDPOINTS.DASHBOARDS.LAYOUT_PATTERNS,
  );
  return data;
};

/** POST /dashboards/layout-patterns */
export const createDashboardLayoutPatternApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  payload: TPayload,
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    API_ENDPOINTS.DASHBOARDS.LAYOUT_PATTERNS,
    payload,
  );
  return data;
};

/** GET /dashboards/:dashboard_code */
export const getDashboardByCodeApi = async (
  dashboardCode: string,
  config?: { signal?: AbortSignal },
): Promise<DashboardByCodeResponse> => {
  const { data } = await api.get<DashboardByCodeResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.DETAIL, {
      dashboard_code: dashboardCode,
    }),
    { signal: config?.signal },
  );
  return data;
};

/** POST /dashboards */
export const createDashboardApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  payload: TPayload,
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    API_ENDPOINTS.DASHBOARDS.LIST,
    payload,
  );
  return data;
};

/** PATCH /dashboards/:dashboard_code */
export const updateDashboardApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  dashboardCode: string,
  payload: TPayload,
): Promise<TResponse> => {
  const { data } = await api.patch<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.DETAIL, {
      dashboard_code: dashboardCode,
    }),
    payload,
  );
  return data;
};

/** PATCH /dashboards/:dashboard_code (name only) */
export const updateDashboardNameApi = async <TResponse = unknown>(
  dashboardCode: string,
  payload: { name: string },
): Promise<TResponse> => {
  const { data } = await api.patch<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.UPDATE_DASHBOARD_NAME, {
      dashboard_code: dashboardCode,
    }),
    payload,
  );

  return data;
};

/** DELETE /dashboards/:dashboard_code */
export const deleteDashboardApi = async <TResponse = void>(
  dashboardCode: string,
): Promise<TResponse> => {
  const { data } = await api.delete<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.DETAIL, {
      dashboard_code: dashboardCode,
    }),
  );
  return data;
};

/** POST /dashboards/:dashboard_code/data */
export const getDashboardDataApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  dashboardCode: string,
  payload?: TPayload,
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.DATA, {
      dashboard_code: dashboardCode,
    }),
    payload ?? {},
  );
  return data;
};

/** PATCH /dashboards/:dashboard_code/layout */
export const updateDashboardLayoutApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  dashboardCode: string,
  payload: TPayload,
): Promise<TResponse> => {
  const { data } = await api.patch<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.LAYOUT, {
      dashboard_code: dashboardCode,
    }),
    payload,
  );
  return data;
};

/** DELETE /dashboards/:dashboard_code/layout */
export const deleteDashboardLayoutApi = async <TResponse = void>(
  dashboardCode: string,
): Promise<TResponse> => {
  const { data } = await api.delete<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.LAYOUT, {
      dashboard_code: dashboardCode,
    }),
  );
  return data;
};

/** POST /dashboards/:dashboard_code/widgets/:template_id/data */
export const getDashboardWidgetDataApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  dashboardCode: string,
  templateId: string | number,
  payload?: TPayload,
  config?: { signal?: AbortSignal },
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.WIDGET_DATA, {
      dashboard_code: dashboardCode,
      template_id: templateId,
    }),
    payload ?? {},
    { signal: config?.signal },
  );
  return data;
};

/** POST /dashboards/:dashboard_code/widgets/:widget_template_id/data/download */
export const exportDashboardWidgetDataApi = async <
  TResponse = unknown,
  TPayload = Record<string, unknown>,
>(
  dashboardCode: string,
  widgetTemplateId: string | number,
  payload?: TPayload,
  format: DashboardWidgetExportFormat = "xlsx",
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.WIDGET_DATA_DOWNLOAD, {
      dashboard_code: dashboardCode,
      widget_template_id: widgetTemplateId,
    }),
    payload ?? {},
    { params: { format } },
  );
  return data;
};

/** DELETE /dashboards/:dashboard_code/widgets/:widget_template_id */
export const deleteDashboardWidgetApi = async <TResponse = void>(
  dashboardCode: string,
  widgetTemplateId: string | number,
): Promise<TResponse> => {
  const { data } = await api.delete<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.WIDGET, {
      dashboard_code: dashboardCode,
      widget_template_id: widgetTemplateId,
    }),
  );
  return data;
};

/** GET /dashboards/:dashboard_code/templates */
export const getDashboardTemplatesApi = async <
  TResponse = unknown,
>(): Promise<TResponse> => {
  const { data } = await api.get<TResponse>(API_ENDPOINTS.DASHBOARDS.TEMPLATES);
  return data;
};

/** DELETE /dashboards/layout-patterns/:pattern_id */
export const deleteDashboardLayoutPatternApi = async <TResponse = void>(
  patternId: string | number,
): Promise<TResponse> => {
  const { data } = await api.delete<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.LAYOUT_PATTERN_DETAIL, {
      pattern_id: patternId,
    }),
  );
  return data;
};

/** POST /dashboards/:dashboard_code/save-as */
export const saveAsDashboardApi = async <TResponse = unknown>(
  dashboardCode: string,
  payload?: { name?: string },
): Promise<TResponse> => {
  const { data } = await api.post<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.SAVE_AS, {
      dashboard_code: dashboardCode,
    }),
    payload ?? {},
  );
  return data;
};

/** PATCH /dashboards/:dashboard_code/default */
export const setDashboardDefaultApi = async <TResponse = unknown>(
  dashboardCode: string,
): Promise<TResponse> => {
  const { data } = await api.patch<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.SET_DEFAULT, {
      dashboard_code: dashboardCode,
    }),
  );
  return data;
};

/** GET /dashboards/:dashboard_code/filters/:filter_key/options */
export const getDashboardFilterOptionsApi = async <TResponse = unknown>(
  dashboardCode: string,
  filterKey: string,
): Promise<TResponse> => {
  const { data } = await api.get<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.FILTER_OPTIONS, {
      dashboard_code: dashboardCode,
      filter_key: filterKey,
    }),
  );
  return data;
};

/** GET /dashboards/:dashboard_code/dropdowns/:dropdown_type */
export const getDashboardDropdownApi = async <TResponse = unknown>(
  dashboardCode: string,
  dropdownType: string,
): Promise<TResponse> => {
  const { data } = await api.get<TResponse>(
    withParams(API_ENDPOINTS.DASHBOARDS.DROPDOWN, {
      dashboard_code: dashboardCode,
      dropdown_type: dropdownType,
    }),
  );
  return data;
};
